import { useEffect, useRef, useState } from 'preact/hooks';
import { Fragment } from 'preact';
import {
  config,
  opened,
  messages,
  visibleMessages,
  inputDraft,
  appendMessage,
  appendDeltaTypewriter,
  finishDeltaTypewriter,
  flushPendingLeadCardFor,
  showLeadCard,
  pendingLeadCard,
  openPendingLeadCard,
  rebindPendingLeadCard,
  updateMessage,
  setOpen,
  webMaximized,
  currentLocale,
  aiThinking,
  activeLeadCard,
  lastLeadCard,
  closeLeadCardWithReopenPill,
  hydrateFromHistory,
  restoreActiveTurn,
  draggedBubblePos,
  webPanelPos,
  webPanelMaxPos,
  widgetClientId,
  fullPage,
} from '../../store/widget-store';
import {
  computePanelStyle,
  placementToStyle,
  WEB_PANEL_W,
  WEB_PANEL_H,
} from '../../hooks/use-bubble-drag';
import { useFrameDrag } from '../../hooks/use-frame-drag';
import type {
  LeadCardShowPayload,
  SystemNoticePayload,
} from '@digital-cs/shared';
import { LeadCaptureCard } from '../LeadCaptureCard';
import { MediaLightbox } from './MediaLightbox';
import { MessageContent, type LightboxTarget } from '../chat/MessageContent';
import { EmojiPicker } from '../chat/EmojiPicker';
import { FaqList } from '../chat/FaqList';
import { OfflineCard } from '../chat/OfflineCard';
import { useImSession } from '../chat/use-im-session';
import { useStickToBottom } from '../chat/use-stick-to-bottom';
import { useBusyHint } from '../chat/use-busy-hint';
import { useI18n } from '../../hooks/use-i18n';
import { SUPPORTED_LOCALES, setLocalePreference } from '../../i18n';
import { pickCsMessage } from '../../i18n/widget-content';
import type {
  SupportedLocale,
  UIMessage,
  AiStreamStartPayload,
  AiStreamDeltaPayload,
  AiStreamEndPayload,
  TurnActivePayload,
  VisitorMessagePayload,
} from '../../types';
import { getInitialSession, getWsClient, resetSession } from '../../services/ws-client';
import {
  IconBot,
  IconGlobe,
  IconMaximize2,
  IconMinimize2,
  IconPaperclip,
  IconRotateCcw,
  IconSend,
  IconSmile,
  IconUser,
  IconX,
} from './icons';

type ChatMode = 'ai' | 'human';

/**
 * 网页客服面板 — 仅 cfg.type === 'web' 时由 App.tsx 渲染。
 * 视觉与交互照搬 cankao/wangyezujian/src/app/components/CustomerServiceWidget.tsx。
 * 数据流接现仓库的 messages / WS streaming / quickQuestions / currentLocale signal,
 * 模式切换、表情、附件未通后端,前端 mock。
 */
export function WebChatPanel() {
  const cfg = config.value;
  const isOpen = opened.value;
  const { t, dir } = useI18n();

  const [chatMode, setChatMode] = useState<ChatMode>('ai');
  // 最大化态提升到 store signal(webMaximized),以便外部 DCSWidget.open({maximized}) 控制;
  // 面板内仍可通过下方按钮/关闭读写。
  const isMaximized = webMaximized.value;
  const [isMobile, setIsMobile] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // IM 图片/视频点击放大查看 —— null 表示遮罩未打开
  const [lightbox, setLightbox] = useState<LightboxTarget | null>(null);

  // 框体拖动:普通态 / 最大化态共用 header 把手,按当前态切换槽位与落点信号;
  // 移动端全屏 / 整页 H5 下禁用(enabled=false,交还 CSS 全屏布局)。
  const frameDrag = useFrameDrag({
    enabled: !isMobile && !fullPage.value,
    posSignal: isMaximized ? webPanelMaxPos : webPanelPos,
    slot: isMaximized ? 'web-panel-max' : 'web-panel',
    clientId: widgetClientId.value,
    fallbackW: isMaximized ? 960 : WEB_PANEL_W,
    fallbackH: isMaximized ? 800 : WEB_PANEL_H,
  });

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const langMenuRef = useRef<HTMLDivElement | null>(null);

  const imEnabled = !!cfg?.im?.enabled;

  // 人工 IM 会话(在线状态/历史/收发/正在输入/翻页)—— 与数字人客服共用 hook,一处维护。
  const im = useImSession(imEnabled && chatMode === 'human', bodyRef);
  const {
    agentOnline,
    imOffline,
    agentTyping,
    imHasMore,
    imLoadingMore,
    loadMoreIm,
    triggerMyTyping,
    prependingRef,
  } = im;

  // 移动端检测 (照搬参考的 <768px 临界点)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // 语言菜单点外面关闭。
  // widget 挂在 open shadow root 里(main.tsx),document 级监听看到的 e.target
  // 会被 retarget 成 shadow host —— 用 e.target 做 contains() 判断会永远返回 false,
  // 导致菜单一打开就被自己的 mousedown 关掉,语言项 onClick 还来不及触发。
  // 必须走 composedPath() 拿真正穿过 shadow 边界的路径。
  useEffect(() => {
    if (!showLanguageMenu) return;
    const onDoc = (e: MouseEvent) => {
      const node = langMenuRef.current;
      if (!node) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (path.includes(node)) return;
      // composedPath 不可用时退化到 target 判断 (老浏览器);shadow DOM 下会误关,但不阻塞主流程
      if (path.length === 0 && node.contains(e.target as Node)) return;
      setShowLanguageMenu(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showLanguageMenu]);

  // SSE 流式事件 + system.notice + lead.card.show — 同 ChatPanel.tsx，但没有数字人状态机
  useEffect(() => {
    const client = getWsClient();
    let cancelled = false;

    // 在飞回合 watchdog:刷新/重连经 turn.active 恢复忙碌态后,若回合事件永远不来
    // (BE 重启等),90s 后自动解锁闸门并收尾 streaming 气泡,防永久卡死。
    let turnWatchdog: ReturnType<typeof setTimeout> | null = null;
    const clearTurnWatchdog = () => {
      if (turnWatchdog) {
        clearTimeout(turnWatchdog);
        turnWatchdog = null;
      }
    };
    const armTurnWatchdog = () => {
      clearTurnWatchdog();
      turnWatchdog = setTimeout(() => {
        turnWatchdog = null;
        aiThinking.value = false;
        for (const m of messages.value) {
          if (m.role === 'ai' && m.streaming) {
            updateMessage(m.id, { streaming: false });
            flushPendingLeadCardFor(m.id); // 有暂存留资卡锚定它则弹出
          }
        }
      }, 90_000);
    };
    // turn.active / session.activeTurn 统一入口(web 线无语音,快照直接显文本)
    const applyActiveTurn = (turn: TurnActivePayload) => {
      restoreActiveTurn(turn);
      armTurnWatchdog();
    };

    // 拿 visitorToken + 打开 SSE 长连接,完成后用历史/pendingCards/在飞回合还原 UI
    void (async () => {
      await client.connect();
      if (cancelled) return;
      const sess = getInitialSession();
      if (sess) {
        // 有在飞回合时延弹历史还原卡:挂暂存位,回合流式结束再弹(不压在回复内容上方)
        hydrateFromHistory(sess.history, sess.pendingCards, t('panel.leadReopenHint'), {
          deferLeadCard: !!sess.activeTurn,
        });
        // 刷新恢复:有在飞回合则恢复忙碌闸门 + 快照气泡(restoreActiveTurn 幂等,
        // 与 SSE 建连时 BE 补推的 turn.active 同源不冲突)
        if (sess.activeTurn) applyActiveTurn(sess.activeTurn);
      }
    })();
    const offStart = client.on('ai.stream.start', (raw) => {
      const p = raw as AiStreamStartPayload;
      aiThinking.value = false;
      // 回合前置阶段暂存的还原卡(空锚)重绑到这条流式消息,回合结束时弹出
      rebindPendingLeadCard(p.messageId);
      const existing = messages.value.find((m) => m.id === p.messageId);
      if (existing) {
        updateMessage(p.messageId, { streaming: true });
      } else {
        appendMessage({
          id: p.messageId,
          role: 'ai',
          text: '',
          ts: Date.now(),
          streaming: true,
        });
      }
    });
    const offDelta = client.on('ai.stream.delta', (raw) => {
      const p = raw as AiStreamDeltaPayload;
      // 打字机缓冲:delta 成批到达(dsh 150ms 合并),按节奏逐字显出而非整段弹出
      appendDeltaTypewriter(p.messageId, p.text);
    });
    const offTurnActive = client.on('turn.active', (raw) => {
      applyActiveTurn(raw as TurnActivePayload);
    });
    const offEnd = client.on('ai.stream.end', (raw) => {
      const p = raw as AiStreamEndPayload;
      // streaming 标记由打字机在缓冲吐完后摘除(finishDeltaTypewriter 内部处理)
      finishDeltaTypewriter(p.messageId);
      aiThinking.value = false;
      clearTurnWatchdog(); // 回合正常收尾,撤 watchdog
    });
    const offNotice = client.on('system.notice', (raw) => {
      const p = raw as SystemNoticePayload;
      appendMessage({
        id: `m-sys-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        role: 'system',
        text: p.text,
        ts: Date.now(),
      });
    });
    const offLeadCard = client.on('lead.card.show', (raw) => {
      const p = raw as LeadCardShowPayload;
      // 弹卡闸门:锚消息还在流式输出(含打字机未吐完)则暂存,输完再弹(store 内处理)
      showLeadCard({ fields: p.fields, prompt: p.prompt, anchorMsgId: p.messageId });
    });
    return () => {
      cancelled = true;
      offStart();
      offDelta();
      offEnd();
      offNotice();
      offLeadCard();
      offTurnActive();
      clearTurnWatchdog();
    };
  }, []);

  // 贴底跟随:用户上滚看历史时暂停吸底(AI 流式期间也可滚动),滚回底部恢复
  const { handleBodyScroll, scrollIfStuck, pinToBottom } = useStickToBottom(bodyRef);
  // 忙碌提示闪现(hook,必须在下方条件 return 之前调用)
  const busyHint = useBusyHint();

  // 自动滚动(翻页 prepend 时跳过一次,避免把视图甩到底;仅贴底时跟随)
  useEffect(() => {
    if (prependingRef.current) {
      prependingRef.current = false;
      return;
    }
    scrollIfStuck();
  }, [messages.value, aiThinking.value, agentTyping, chatMode]);

  if (!cfg || !isOpen) return null;

  const draft = inputDraft.value;
  // 按当前模式隔离会话:人工模式只显人工 IM 记录,AI 模式只显 AI 对话
  const visible = visibleMessages(messages.value, chatMode);
  // 留资卡锚点:卡在触发它的 AI 回答下方内联渲染;锚点缺失(旧数据/锚消息不在可见列表)
  // 时回落到消息流末尾(底部兜底渲染)。
  const leadCardAnchorId = activeLeadCard.value?.anchorMsgId ?? null;
  const leadCardAnchored =
    !!leadCardAnchorId && visible.some((m) => m.id === leadCardAnchorId);
  // 留资再入口 pill 锚点:与卡片同款逻辑,pill 钉在触发回答正下方而非沉到消息流末尾
  const leadPillMsg = visible.find((m) => m.meta?.kind === 'lead-reopen') ?? null;
  const leadPillAnchorId = leadPillMsg?.meta?.anchorMsgId ?? null;
  const leadPillAnchored =
    !!leadPillMsg && !!leadPillAnchorId && visible.some((m) => m.id === leadPillAnchorId);
  // 暂存期 pill(弹卡闸门持有,回复中可点提前展开):锚定正在输出的气泡下方,跟随下移
  const pendingPillAnchorId = pendingLeadCard.value?.anchorMsgId || null;
  const pendingPillAnchored =
    !!pendingPillAnchorId && visible.some((m) => m.id === pendingPillAnchorId);

  /** 留资再入口 pill(dismiss/收起后,消息列表里的 system 消息形态) */
  const renderLeadReopenPill = (m: UIMessage) => (
    <div key={m.id} class="dcs-msg dcs-web-system">
      <button
        type="button"
        class="dcs-web-system-pill dcs-lead-reopen-pill"
        onClick={() => {
          if (lastLeadCard.value) {
            activeLeadCard.value = lastLeadCard.value;
          }
        }}
        aria-label={m.text}
      >
        <span class="dcs-lead-reopen-icon" aria-hidden="true">▶</span>
        {m.text}
      </button>
    </div>
  );

  /** 暂存期 pill(派生 UI,不进消息列表):点击立即展开并清暂存 */
  const renderPendingLeadPill = () => (
    <div class="dcs-msg dcs-web-system">
      <button
        type="button"
        class="dcs-web-system-pill dcs-lead-reopen-pill"
        onClick={openPendingLeadCard}
        aria-label={t('panel.leadReopenHint')}
      >
        <span class="dcs-lead-reopen-icon" aria-hidden="true">▶</span>
        {t('panel.leadReopenHint')}
      </button>
    </div>
  );
  // AI 回合进行中(思考/流式)禁发,防并发回合异常(BE 另有 per-convId 串行兜底);
  // 仅 AI 模式闸门,人工 IM 消息不触发回合,不拦。
  const aiBusy =
    chatMode === 'ai' &&
    (aiThinking.value || visible.some((m) => m.role === 'ai' && m.streaming === true));

  const sendText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // 防御:UI 按钮/回车已拦,这里兜底
    if (aiBusy) return;
    pinToBottom(); // 自己发消息必须立即可见,恢复贴底跟随
    // 新回合自动收起还开着的留资卡(留「再开」pill 锚在流内)
    closeLeadCardWithReopenPill();
    if (chatMode === 'human' && imEnabled) {
      // 人工模式:hook 负责乐观展示访客气泡 + 发到 vocechat(WebSocket 回显按 mid/client_msg_id 合并)。
      im.sendText(trimmed);
    } else {
      appendMessage({
        id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        role: 'visitor',
        text: trimmed,
        ts: Date.now(),
      });
      if (chatMode === 'ai') {
        aiThinking.value = true;
        const payload: VisitorMessagePayload = { text: trimmed, msgType: 'text' };
        getWsClient().emit('visitor.message', payload);
      }
      // 人工模式但未启用 vocechat — 仅保留访客气泡,不发。
    }
    inputDraft.value = '';
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.focus();
    }
  };

  const sendDraft = () => sendText(draft);

  const handleResetChat = async () => {
    // 1) 通知后端 close 旧 conv → 清前端 store
    await resetSession();
    messages.value = [];
    inputDraft.value = '';
    aiThinking.value = false;
    activeLeadCard.value = null;
    lastLeadCard.value = null;
    pendingLeadCard.value = null;
    // 2) 重新 connect 拿新 convId+token,新会话 history 必然为空,
    //    hydrate 走兜底分支不会再灌历史。
    const client = getWsClient();
    await client.connect();
    const sess = getInitialSession();
    if (sess) hydrateFromHistory(sess.history, sess.pendingCards, t('panel.leadReopenHint'));
  };

  const toggleChatMode = () => {
    // 仅切换模式。人工招呼由人工聊天区顶部常驻招呼行渲染(主体仅在坐席在线时渲染,
    // 故招呼天然只在接通后出现),不再往消息流追加「转接中」系统提示;AI 招呼同样由顶部恒显欢迎行承担。
    setChatMode((prev) => (prev === 'ai' ? 'human' : 'ai'));
  };

  const handleEmojiSelect = (emoji: string) => {
    inputDraft.value = (inputDraft.value || '') + emoji;
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  const handleFileUpload = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;
    if (imEnabled && chatMode === 'human') {
      // 人工模式:hook 负责乐观展示(本地 blob 预览)+ 后台上传发送;WebSocket 回显合并。
      im.sendFile(file);
    } else {
      // 非 IM:仅本地占位(原行为)
      appendMessage({
        id: `f-${Date.now()}`,
        role: 'visitor',
        text: file.name,
        ts: Date.now(),
      });
    }
    target.value = '';
  };

  const showOfflineCard = chatMode === 'human' && !agentOnline;
  const showInput = !showOfflineCard;

  // 容器尺寸 class — 移动全屏 / 最大化 / 默认;拖动中加 is-dragging(切 grabbing 光标)
  const containerCls =
    (isMobile
      ? 'dcs-web-panel is-mobile'
      : isMaximized
      ? 'dcs-web-panel is-maximized'
      : 'dcs-web-panel') + (frameDrag.dragging ? ' is-dragging' : '');

  // 框体定位优先级:拖动中实时 dragStyle > 本态独立落点(placementToStyle)>
  // [仅普通态]跟随气泡落点(computePanelStyle)> CSS 默认(服务端 --dcs-offset-* / 最大化居中)。
  // 最大化态独立落点须叠加 transform:none 抵消 .is-maximized 的 translate(50%,50%)。
  // 移动端全屏 / 整页 H5 下忽略所有落点,交还 CSS 类布局。
  const dragEnabled = !isMobile && !fullPage.value;
  const persistedStyle = !dragEnabled
    ? undefined
    : isMaximized
      ? webPanelMaxPos.value
        ? { ...placementToStyle(webPanelMaxPos.value), transform: 'none' }
        : undefined
      : webPanelPos.value
        ? placementToStyle(webPanelPos.value)
        : draggedBubblePos.value
          ? computePanelStyle(draggedBubblePos.value)
          : undefined;
  const panelStyle = frameDrag.dragStyle ?? persistedStyle;

  return (
    <>
      {isMaximized && !isMobile && (
        <div class="dcs-web-backdrop" aria-hidden="true" onClick={() => { webMaximized.value = false; }} />
      )}
      <section
        class={containerCls}
        role="dialog"
        aria-label={pickCsMessage(cfg, 'name', currentLocale.value)}
        aria-modal="false"
        dir={dir}
        style={panelStyle}
      >
        {/* Header（兼作拖动把手:空白区可拖,内部按钮经 hitInteractive 自动排除） */}
        <header
          class="dcs-web-header"
          {...frameDrag.handleProps}
          style={cfg.titleBgGradient ? { background: cfg.titleBgGradient } : undefined}
        >
          <div class="dcs-web-header-left">
            <div class="dcs-web-header-avatar">
              {cfg.avatarUrl ? (
                <img src={cfg.avatarUrl} alt="" />
              ) : chatMode === 'ai' ? (
                <IconBot size={22} />
              ) : (
                <IconUser size={22} />
              )}
            </div>
            <div class="dcs-web-header-text">
              <h3 class="dcs-web-header-title">
                {chatMode === 'ai' ? t('web.aiChat') : t('web.humanChat')}
              </h3>
              <div class="dcs-web-header-status">
                {chatMode === 'ai' || agentOnline ? (
                  <>
                    <span class="dcs-web-dot is-online" aria-hidden="true" />
                    <span>{t('panel.onlineStatus')}</span>
                  </>
                ) : (
                  <>
                    <span class="dcs-web-dot is-offline" aria-hidden="true" />
                    <span>{t('web.offlineLabel')}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div class="dcs-web-header-actions" data-dcs-nodrag>
            {/* 重置仅在 AI 会话可用;人工 IM 聊天不允许重置(会话归坐席侧管理) */}
            {chatMode === 'ai' && messages.value.length > 0 && (
              <button
                type="button"
                class="dcs-web-icon-btn"
                title={t('web.resetChat')}
                aria-label={t('web.resetChat')}
                onClick={handleResetChat}
              >
                <IconRotateCcw size={18} />
              </button>
            )}

            <button
              type="button"
              class="dcs-web-icon-btn"
              title={chatMode === 'ai' ? t('web.switchToHuman') : t('web.switchToAI')}
              aria-label={chatMode === 'ai' ? t('web.switchToHuman') : t('web.switchToAI')}
              onClick={toggleChatMode}
            >
              {chatMode === 'ai' ? <IconUser size={18} /> : <IconBot size={18} />}
            </button>

            <div class="dcs-web-lang" ref={langMenuRef}>
              <button
                type="button"
                class="dcs-web-icon-btn"
                aria-haspopup="listbox"
                aria-expanded={showLanguageMenu}
                aria-label={t('web.selectLanguage')}
                title={t('web.selectLanguage')}
                onClick={() => setShowLanguageMenu((v) => !v)}
              >
                <IconGlobe size={18} />
              </button>
              {showLanguageMenu && (
                <ul class="dcs-web-lang-menu" role="listbox">
                  {SUPPORTED_LOCALES.map((l) => (
                    <li key={l.value} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={currentLocale.value === l.value}
                        class={`dcs-web-lang-item${currentLocale.value === l.value ? ' is-active' : ''}`}
                        onClick={() => {
                          currentLocale.value = l.value as SupportedLocale;
                          setLocalePreference(l.value as SupportedLocale);
                          setShowLanguageMenu(false);
                        }}
                      >
                        {l.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {/* native <select> kept hidden for any external test selectors */}
              <select
                class="dcs-lang-select dcs-web-sr-select"
                aria-hidden="true"
                tabIndex={-1}
                value={currentLocale.value}
                onChange={(e) => {
                  const v = (e.currentTarget as HTMLSelectElement).value as SupportedLocale;
                  currentLocale.value = v;
                  setLocalePreference(v);
                }}
              >
                {SUPPORTED_LOCALES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 整页 H5 模式:面板已恒定铺满,最大化/还原是 no-op,隐藏避免困惑 */}
            {!isMobile && !fullPage.value && (
              <button
                type="button"
                class="dcs-web-icon-btn"
                title={isMaximized ? t('web.restore') : t('web.maximize')}
                aria-label={isMaximized ? t('web.restore') : t('web.maximize')}
                onClick={() => { webMaximized.value = !webMaximized.value; }}
              >
                {isMaximized ? <IconMinimize2 size={18} /> : <IconMaximize2 size={18} />}
              </button>
            )}

            {/* 整页 H5 模式:整页即客服,关闭后只剩空白且无气泡可重开,隐藏;退出交给小程序原生导航 */}
            {!fullPage.value && (
              <button
                type="button"
                class="dcs-web-icon-btn"
                title={t('panel.closeAria')}
                aria-label={t('panel.closeAria')}
                onClick={() => {
                  webMaximized.value = false;
                  setOpen(false);
                }}
              >
                <IconX size={18} />
              </button>
            )}
          </div>
        </header>

        {/* Body */}
        <div
          class="dcs-web-body"
          role="log"
          aria-live="polite"
          aria-label={pickCsMessage(cfg, 'name', currentLocale.value)}
          ref={bodyRef}
          onScroll={(e) => {
            const el = e.currentTarget as HTMLDivElement;
            handleBodyScroll(el);
            // 人工模式上滑到顶 → 加载更早历史(滚动位置补偿在 loadMoreIm 内做)
            if (chatMode !== 'human' || !imEnabled) return;
            if (el.scrollTop <= 24 && imHasMore && !imLoadingMore) {
              void loadMoreIm();
            }
          }}
        >
          {showOfflineCard ? (
            <OfflineCard
              cfg={cfg}
              imOffline={imOffline}
              onBackToAi={toggleChatMode}
              t={t}
            />
          ) : (
            <>
              {chatMode === 'ai' && (
                <div class="dcs-web-welcome">
                  <span class="dcs-msg-avatar dcs-web-msg-avatar is-ai" aria-hidden="true">
                    {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconBot size={18} />}
                  </span>
                  <div class="dcs-web-bubble-text dcs-web-bubble-ai">
                    {pickCsMessage(cfg, 'welcomeMsg', currentLocale.value) ||
                      t('web.aiGreeting')}
                  </div>
                </div>
              )}

              {/* 人工招呼行 —— 转人工接通后(主体仅在 agentOnline 时渲染)主动打招呼引导访客,
                  对标 AI 欢迎行;头像兜底用人工图标。文案取 humanWelcomeMsg,空则回退内置招呼语。 */}
              {chatMode === 'human' && (
                <div class="dcs-web-welcome">
                  <span class="dcs-msg-avatar dcs-web-msg-avatar is-ai" aria-hidden="true">
                    {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconUser size={18} />}
                  </span>
                  <div class="dcs-web-bubble-text dcs-web-bubble-ai">
                    {pickCsMessage(cfg, 'humanWelcomeMsg', currentLocale.value) ||
                      t('web.humanGreeting')}
                  </div>
                </div>
              )}

              {/* FAQ 列表 — 仅在欢迎页(尚未发起对话)且后台配置了 FAQ 时展示;
                  点击 question 就地展开 answer,answer 直接来自 admin 配置,不发 WS。 */}
              {chatMode === 'ai' && visible.length === 0 && cfg.faqs.length > 0 && (
                <FaqList faqs={cfg.faqs} locale={currentLocale.value} t={t} />
              )}

              {visible.map((m) => {
                if (m.role === 'system') {
                  // 留资再入口型系统消息:渲染成可点 button,点击重新打开卡片。
                  // 已提交过(lastLeadCard 为 null)就把所有此类 pill 隐藏,不在历史里留可点死链。
                  if (m.meta?.kind === 'lead-reopen') {
                    if (lastLeadCard.value === null) return null;
                    // 锚定命中:跳过原始位置,挪到锚消息正下方渲染(见下方消息循环)
                    if (leadPillAnchored && m === leadPillMsg) return null;
                    return renderLeadReopenPill(m);
                  }
                  return (
                    <div key={m.id} class="dcs-msg dcs-web-system">
                      <div class="dcs-web-system-pill">{m.text}</div>
                    </div>
                  );
                }
                const isVisitor = m.role === 'visitor';
                return (
                  <Fragment key={m.id}>
                    <div
                      class={`dcs-msg dcs-msg-${m.role} dcs-web-row${isVisitor ? ' is-visitor' : ''}`}
                    >
                      <span class="dcs-sr-only">{t(`roles.${m.role}`)}</span>
                      {!isVisitor && (
                        <span class="dcs-msg-avatar dcs-web-msg-avatar is-ai" aria-hidden="true">
                          {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconBot size={18} />}
                        </span>
                      )}
                      <div
                        class={`dcs-msg-text dcs-web-bubble-text ${
                          isVisitor ? 'dcs-web-bubble-user' : 'dcs-web-bubble-ai'
                        }`}
                      >
                        <MessageContent msg={m} t={t} onOpenLightbox={setLightbox} />
                      </div>
                      {isVisitor && (
                        <span class="dcs-msg-avatar dcs-web-msg-avatar is-user" aria-hidden="true">
                          <IconUser size={18} />
                        </span>
                      )}
                    </div>
                    {/* 留资卡锚定渲染:贴在触发它的这条 AI 回答正下方 */}
                    {leadCardAnchorId === m.id && <LeadCaptureCard />}
                    {/* 留资 pill 锚定渲染:钉在触发回答正下方(原位已跳过) */}
                    {leadPillAnchored && leadPillAnchorId === m.id && renderLeadReopenPill(leadPillMsg!)}
                    {/* 暂存期 pill:回复进行中可点提前展开 */}
                    {pendingPillAnchored && pendingPillAnchorId === m.id && renderPendingLeadPill()}
                  </Fragment>
                );
              })}

              {chatMode === 'ai' && aiThinking.value && (
                <div
                  class="dcs-msg dcs-msg-ai dcs-web-row"
                  role="status"
                  aria-label={t('panel.thinkingAria')}
                >
                  <span class="dcs-msg-avatar dcs-web-msg-avatar is-ai" aria-hidden="true">
                    {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconBot size={18} />}
                  </span>
                  <div class="dcs-msg-text dcs-web-bubble-text dcs-web-bubble-ai dcs-thinking-dots" aria-hidden="true">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}

              {/* 人工坐席「正在输入」提示(vocechat WebSocket typing 事件;3s 无新事件自动清除) */}
              {chatMode === 'human' && agentTyping && (
                <div class="dcs-msg dcs-msg-human dcs-web-row" role="status">
                  <span class="dcs-msg-avatar dcs-web-msg-avatar is-ai" aria-hidden="true">
                    {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconUser size={18} />}
                  </span>
                  <div class="dcs-msg-text dcs-web-bubble-text dcs-web-bubble-ai dcs-thinking-dots" aria-hidden="true">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}

              {/* 留资卡兜底渲染:锚点缺失(旧数据/锚消息不可见)时落在消息流末尾;
                  锚定生效时已在上方 map 里内联渲染,这里不再重复。
                  LeadCaptureCard 自身 useEffect 在挂载时把卡片 scrollIntoView 滚进视口。 */}
              {!leadCardAnchored && <LeadCaptureCard />}
              {/* 暂存期 pill 兜底:锚点缺失(空锚待重绑/锚消息不可见)时落在消息流末尾 */}
              {pendingLeadCard.value && !pendingPillAnchored && renderPendingLeadPill()}
            </>
          )}
        </div>

        {/* Footer / input */}
        {showInput && (
          <footer class="dcs-web-footer">
            {showEmojiPicker && chatMode === 'human' && (
              <EmojiPicker onSelect={handleEmojiSelect} />
            )}
            <div class="dcs-web-input-wrap">
              <textarea
                ref={inputRef}
                class="dcs-web-input"
                placeholder={t('panel.inputPlaceholder')}
                rows={1}
                value={draft}
                onInput={(e) => {
                  const ta = e.currentTarget as HTMLTextAreaElement;
                  inputDraft.value = ta.value;
                  ta.style.height = 'auto';
                  ta.style.height = Math.min(ta.scrollHeight, 128) + 'px';
                  triggerMyTyping();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (aiBusy) {
                      busyHint.flash(); // 回合进行中:给提示,不发送
                      return;
                    }
                    sendDraft();
                  }
                }}
                aria-label={t('panel.inputPlaceholder')}
              />
              <div class="dcs-web-input-actions">
                {/* 忙碌提示小气泡:点击禁用态发送按钮/按回车时在按钮上方弹出 */}
                {aiBusy && busyHint.visible && (
                  <div class="dcs-send-busy-tip" role="status">
                    {t('panel.aiBusyHint')}
                  </div>
                )}
                {chatMode === 'human' && (
                  <>
                    <button
                      type="button"
                      class="dcs-web-input-icon"
                      onClick={() => setShowEmojiPicker((v) => !v)}
                      aria-label="emoji"
                    >
                      <IconSmile size={20} />
                    </button>
                    <button
                      type="button"
                      class="dcs-web-input-icon"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="attach"
                    >
                      <IconPaperclip size={20} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      class="dcs-web-file-input"
                      accept="image/*,video/*,.pdf,.doc,.docx"
                      onChange={handleFileUpload}
                    />
                  </>
                )}
                <button
                  type="button"
                  class={`dcs-send-btn dcs-web-send-btn${aiBusy ? ' is-busy' : ''}`}
                  aria-label={t('panel.sendAria')}
                  disabled={!draft.trim()}
                  aria-disabled={aiBusy || undefined}
                  title={aiBusy ? t('panel.aiBusyHint') : t('panel.sendAria')}
                  onClick={() => {
                    // 忙碌时按钮保留可点(原生 disabled 不触发 click,给不了反馈):点击闪现提示
                    if (aiBusy) {
                      busyHint.flash();
                      return;
                    }
                    sendDraft();
                  }}
                  style={
                    cfg.theme
                      ? { background: cfg.theme, '--dcsw-send-theme': cfg.theme }
                      : undefined
                  }
                >
                  <IconSend size={16} />
                </button>
              </div>
            </div>
          </footer>
        )}
      </section>

      {/* 图片/视频放大查看遮罩 —— 必须放在 .dcs-web-panel 之外:
          最大化时 panel 带 transform 会成为 fixed 定位的包含块,放里面会无法铺满视口。
          key={url} 保证每次打开/换源都重新挂载,缩放/平移状态自动归零。 */}
      {lightbox && (
        <MediaLightbox
          key={lightbox.url}
          type={lightbox.type}
          url={lightbox.url}
          name={lightbox.name}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}
